import { useState, useCallback, useRef, useEffect, useContext } from 'react'
import { useDigifiSearchCore } from './Core/useDigifiSearchCore'
import { useLocation } from 'react-router-dom'
import { DIGIFI_DEFAULT_PAGE, DIGIFI_DEFAULT_PAGE_SIZE, DIGIFI_FILTER_FIELD_NAMES } from '../constants'
import { getSearchParamsFromUrl, sortFiltersByPriority, syncFiltersWithUrlParams } from '../Helpers'
import { SiteContext } from '../../Global/SiteContext'
import { useScrollToElement } from '../../../hooks/useScrollToElement'

const IMMUTABLE_FIELDS = [DIGIFI_FILTER_FIELD_NAMES.PAGE_SIZE]
const MULTI_SELECT_FIELDS = [DIGIFI_FILTER_FIELD_NAMES.BRAND]
const SINGLE_SELECT_FIELDS = [DIGIFI_FILTER_FIELD_NAMES.SORT_BY]
const TOGGLEABLE_FIELDS = [DIGIFI_FILTER_FIELD_NAMES.PREBOOK]
const NUMERIC_RANGE_FIELDS = [DIGIFI_FILTER_FIELD_NAMES.PRICE]

const getInitialFilters = () => [
   {
      fieldName: DIGIFI_FILTER_FIELD_NAMES.PAGE_SIZE,
      value: DIGIFI_DEFAULT_PAGE_SIZE,
   },
   {
      fieldName: DIGIFI_FILTER_FIELD_NAMES.PAGE,
      value: DIGIFI_DEFAULT_PAGE,
   },
]

/**
 * Hook for managing product search functionality with filters and pagination
 * @param {Object} defaultFilter - The default filter to apply on initial load (e.g. {fieldName: 'filterCategories', value: 123})
 * @param {boolean} isSearchPage - Whether this is being used on the search page
 * @returns {Object} Search state and methods
 * @returns {Array} .filters - Current active filters
 * @returns {Array} .products - Found products
 * @returns {Object} .facets - Available facets for filtering
 * @returns {Object} .metaData - Search metadata like total count
 * @returns {boolean} .loading - Whether search is in progress
 * @returns {string} .errorMessage - Error message if search failed
 * @returns {Function} .updateFilters - Method to update active filters
 * @returns {Function} .resetTagFilters - Method to reset filters
 */
export const useDigifiProductSearch = (defaultFilter, isSearchPage = false) => {
   const { searchProducts, loading, errorMessage } = useDigifiSearchCore()
   const scrollToElement = useScrollToElement()
   const location = useLocation()
   const [filters, setFilters] = useState(() => getSearchParamsFromUrl(getInitialFilters(), IMMUTABLE_FIELDS, location))
   const [products, setProducts] = useState(null)
   const [facets, setFacets] = useState(null)
   const [metaData, setMetaData] = useState(null)
   // const hasPersistedValues = useRef(false)
   const isSearchPageRef = useRef(defaultFilter?.fieldName === DIGIFI_FILTER_FIELD_NAMES.SEARCH_QUERY || isSearchPage)
   const { showVat } = useContext(SiteContext)
   const isInitialRender = useRef(true)
   const initialTimeoutRef = useRef(null);

   const removeFilters = (filters, fieldName) => {
      return filters.filter(f => f.fieldName !== fieldName)
   }

   const normalizeFilterValue = (fieldName, value, existingFilter) => {
      try {
         if (MULTI_SELECT_FIELDS.includes(fieldName)) {
            // Ensure the existing value is an array
            const currentValues = Array.isArray(existingFilter?.value) ? existingFilter.value : [];
            const newValues = currentValues.includes(value)
               ? currentValues.filter(v => v !== value) // Remove value if it exists
               : [...currentValues, value]; // Add value if it doesn't exist
   
            return newValues.length > 0 ? newValues : null;
         }
   
         if (SINGLE_SELECT_FIELDS.includes(fieldName)) {
            // Ensure the value is not an array or object (should be a single string/number)
            if (typeof value === "string" || typeof value === "number") {
               return value;
            }
            // console.warn(`Invalid single select value for ${fieldName}:`, value);
            return null;
         }
   
         if (TOGGLEABLE_FIELDS.includes(fieldName)) {
            return value === true || value === "true" ? true : null;
         }
         
         if (NUMERIC_RANGE_FIELDS.includes(fieldName)) {
            if (
               Array.isArray(value) &&
               value.length === 2 &&
               value.every(v => typeof v === "number" && !isNaN(v))
            ) {
               return value;
            }
            // console.warn(`Invalid numeric range value for ${fieldName}:`, value);
            return null;
         }
   
         //  Default case (return value as-is)
         return value;
      } catch (error) {
         // console.error(`Error normalizing filter value for ${fieldName}:`, error);
         return null;
      }
   };

   const updateFilters = useCallback(newFilters => {
      if (!newFilters || !Array.isArray(newFilters)) return

      setFilters(prevFilters => {
         let updatedFilters = [...prevFilters]

         newFilters?.forEach(({ fieldName, value }) => {
            // Prevent modification of immutable fields
            if (IMMUTABLE_FIELDS.includes(fieldName)) return

            // Find the index and object of the existing filter
            const existingFilterIndex = updatedFilters.findIndex(
               f => f.fieldName === fieldName
            )
            const existingFilter =
               existingFilterIndex !== -1
                  ? updatedFilters[existingFilterIndex]
                  : null

            // Normalize the new filter value based on its type
            const normalizedValue = normalizeFilterValue(
               fieldName,
               value,
               existingFilter
            )

            if (
               normalizedValue === null ||
               normalizedValue === undefined ||
               normalizedValue === ''
            ) {
               // Remove filter if the value is invalid
               updatedFilters = removeFilters(updatedFilters, fieldName)
            } else {
               // Update the existing filter or add a new one
               if (existingFilterIndex !== -1) {
                  updatedFilters[existingFilterIndex].value = normalizedValue
               } else {
                  updatedFilters.push({ fieldName, value: normalizedValue })
               }
            }
         })

         // Reset page to 1 if any filter other than 'page' is changed
         if (newFilters.some(f => f.fieldName !== DIGIFI_FILTER_FIELD_NAMES.PAGE)) {
            const pageFilterIndex = updatedFilters.findIndex(
               f => f.fieldName === DIGIFI_FILTER_FIELD_NAMES.PAGE
            )
            if (pageFilterIndex !== -1) {
               updatedFilters[pageFilterIndex].value = 1
            } else {
               updatedFilters.push({ fieldName: DIGIFI_FILTER_FIELD_NAMES.PAGE, value: 1 })
            }
         }

         // Apply sorting before updating state
         return updatedFilters
      })
   }, [])

   const resetTagFilters = useCallback(
      ({ filtersByFieldNameToPreserve = [] } = {}) => {
         setFilters(prevFilters => {
            // Get filters that should be preserved based on field names
            const preservedFilters = prevFilters?.filter(f =>
               filtersByFieldNameToPreserve.includes(f?.fieldName)
            )

            // Get fresh set of default filters
            const newDefaultFilters = getInitialFilters()

            // For each preserved filter, either update existing one or add new
            preservedFilters.forEach(preservedFilter => {
               // Check if this preserved filter already exists in defaults
               const existingFilterIndex = newDefaultFilters.findIndex(
                  f => f.fieldName === preservedFilter.fieldName
               )

               if (existingFilterIndex > -1) {
                  // If filter exists in defaults, override with preserved value
                  newDefaultFilters[existingFilterIndex] = preservedFilter
               } else {
                  // If filter doesn't exist in defaults, add it
                  newDefaultFilters.push(preservedFilter)
               }
            })
            return newDefaultFilters
         })
      },
      []
   )

   useEffect(() => {
      const priceFilter = filters.find(f => f.fieldName === DIGIFI_FILTER_FIELD_NAMES.PRICE);
      if (priceFilter?.value?.length === 2) {
         const [min, max] = priceFilter.value;
         const updatedPrices = showVat 
            ? [min * 1.25, max * 1.25]
            : [min / 1.25, max / 1.25];
         
         updateFilters?.([
            {
               fieldName: DIGIFI_FILTER_FIELD_NAMES.PRICE,
               value: updatedPrices.map(price => Math.round(price)),
            },
         ]);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [showVat])

   useEffect(() => {
      if (!defaultFilter?.fieldName || !defaultFilter?.value) return

      const fetchData = async () => {
         const searchResp = await searchProducts({
            filters: [...filters],
            ...(defaultFilter.fieldName === DIGIFI_FILTER_FIELD_NAMES.SEARCH_QUERY
               ? { defaultSearchQuery: defaultFilter.value }
               : { defaultFilters: [defaultFilter] }),
            showVat
         })

         setProducts(searchResp?.products)
         setFacets(searchResp?.facets)
         setMetaData(searchResp?.metaData)
         // Update URL params after search is complete
         syncFiltersWithUrlParams(filters, isSearchPageRef.current)

         // Only scroll if initial timeout has passed
         if (!isInitialRender.current) {
            scrollToElement('.filter-tags-wrapper')
         }
      }

      fetchData()

      // Set up initial 2-second timeout if not already set
      if (isInitialRender.current) {
         initialTimeoutRef.current = setTimeout(() => {
            isInitialRender.current = false
         }, 2000)
      }

      // Cleanup
      return () => {
         if (initialTimeoutRef.current) {
            clearTimeout(initialTimeoutRef.current)
         }
      }

      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [filters, defaultFilter?.value, defaultFilter?.fieldName, showVat, scrollToElement  ])

   return {
      filters,
      products,
      facets,
      metaData,
      loading,
      errorMessage,
      updateFilters,
      resetTagFilters,
   }
}
